import Images from "../../../../../Images"

// Reusable onboarding guidance for every merchant-provided evidence type (the
// Evidence Vault upload slots). Each entry carries the fields the reviewer asked
// for: Description, Why It Matters, Supports, Classification, Strength, and
// Collection Guidance. Surfaced via a "Guidance" popover next to each slot in
// Configure, and reusable anywhere an evidence type is shown.
//
// Strings are English; the UI wraps each in t() (keys added to en.json). Keep the
// prose plain and factual (issuer-appropriate), not marketing copy.

// Representment strength, strongest first (mirrors how the evidence engine orders
// points in the letter). Compelling = decisive on its own; Strong = materially
// moves a case; Foundational = sets the baseline record; Supporting = corroborates.
export const EVIDENCE_STRENGTHS = {
  compelling: "Compelling",
  strong: "Strong",
  foundational: "Foundational",
  supporting: "Supporting"
}

// How a merchant should treat each evidence type. Required = every merchant should
// provide it; Recommended = broadly useful, provide if you can; Conditional = only
// relevant to certain business types or dispute reasons (we only ask when it applies).
export const EVIDENCE_CLASSIFICATIONS = {
  required: "Required",
  recommended: "Recommended",
  conditional: "Conditional"
}

export const EVIDENCE_GUIDANCE = {
  order_confirmation_screenshot: {
    description:
      'The order-confirmation or "thank you" page a buyer sees right after a successful purchase.',
    whyItMatters:
      'Shows the buyer completed the purchase knowingly and received the order details, which counters "I never made this purchase" and "I don\'t recognize this charge" claims.',
    supports:
      "Fraud and unrecognized-charge disputes, and product-not-received disputes (proves the order existed).",
    classification: "recommended",
    strength: "foundational",
    collection:
      "Place a test order (or use a real one) and screenshot the confirmation page showing the order number, items, amount, and date."
  },
  payment_receipt_email: {
    description: "The receipt email customers receive after a charge.",
    whyItMatters:
      'Demonstrates the cardholder was shown exactly what they paid and for what, undercutting "I was charged the wrong amount" and "I did not authorize this" claims.',
    supports: "Processing-error and fraud / unrecognized disputes.",
    classification: "recommended",
    strength: "strong",
    collection:
      "Forward or screenshot a real receipt email showing the amount, line items, billing descriptor, and date. Redact unrelated personal data."
  },
  refund_confirmation_email: {
    description: "The email confirming a refund was issued.",
    whyItMatters:
      'When a buyer claims "I never got my refund", this proves a refund was processed, often resolving the dispute outright.',
    supports: "Credit-not-processed disputes.",
    classification: "conditional",
    strength: "strong",
    collection:
      "Screenshot a refund-confirmation email showing the refunded amount, order reference, and the date the refund was sent."
  },
  return_policy_document: {
    description: "Your published return policy document or page (physical goods).",
    whyItMatters:
      'Shows the return terms and window the cardholder agreed to, countering "the item could not be returned" and similar consumer claims.',
    supports: "Consumer disputes about returns of physical goods.",
    classification: "conditional",
    strength: "strong",
    collection:
      "Upload a screenshot or PDF of your live return policy showing the page URL, the return window, and conditions."
  },
  account_creation_email: {
    description: "The welcome / account-creation email sent when a customer signs up.",
    whyItMatters:
      "Ties the cardholder to a real account they created and accepted your terms through, helping prove authorization on fraud claims.",
    supports: "Fraud / unrecognized disputes and subscription disputes (account ownership).",
    classification: "conditional",
    strength: "foundational",
    collection:
      "Screenshot a real welcome email showing the account email, sign-up date, and any terms-acceptance link."
  },
  checkout_screen_screenshot: {
    description:
      "Your checkout page showing the prices, totals, and terms a buyer agrees to before paying.",
    whyItMatters:
      "Proves what the customer saw and agreed to at the moment of purchase (terms, totals, and policies), which is central to authorization and terms-acceptance arguments.",
    supports: "Most dispute types: fraud, consumer disputes, subscription, and processing errors.",
    classification: "required",
    strength: "compelling",
    collection:
      "Screenshot the final checkout / review step showing the order total, the terms checkbox or link, and any policy disclosures."
  },
  tracking_page_screenshot: {
    description: "The carrier tracking page customers land on to follow their delivery.",
    whyItMatters:
      'Carrier tracking through to a delivered status is the single strongest evidence against "I never received my order".',
    supports: "Product-not-received disputes (physical goods).",
    classification: "conditional",
    strength: "compelling",
    collection:
      "Open a real tracking link and screenshot the page showing the tracking number, carrier, and a delivered status with date."
  },
  cancellation_flow_screenshot: {
    description: "The self-serve flow a subscriber uses to cancel.",
    whyItMatters:
      'Proves the customer could have cancelled on their own at any time, a strong rebuttal to "I was charged after I cancelled" disputes.',
    supports: "Subscription-cancelled disputes (recurring billing).",
    classification: "conditional",
    strength: "compelling",
    collection:
      "Screenshot the in-account cancellation screen (the button or flow), ideally showing it is reachable in a couple of clicks."
  },
  renewal_reminder_email: {
    description: "The reminder email sent before a subscription renews.",
    whyItMatters:
      'Shows the customer was told in advance that a renewal charge was coming, countering "I did not know I would be billed again".',
    supports: "Subscription / recurring-billing disputes.",
    classification: "conditional",
    strength: "supporting",
    collection:
      "Screenshot a real renewal-reminder email showing the renewal date, amount, and a link to manage or cancel."
  }
}

// Evidence-locker upload slots → their "Example" illustration. Hovering the Example tag
// on a slot shows a styled mockup of what a good upload looks like.
export const EVIDENCE_EXAMPLES = {
  order_confirmation_screenshot: Images.evidence_order_confirmation,
  payment_receipt_email: Images.evidence_payment_receipt,
  refund_confirmation_email: Images.evidence_refund_confirmation,
  account_creation_email: Images.evidence_account_creation,
  checkout_screen_screenshot: Images.evidence_checkout_screen,
  tracking_page_screenshot: Images.evidence_tracking_page,
  cancellation_flow_screenshot: Images.evidence_cancellation_flow,
  renewal_reminder_email: Images.evidence_renewal_reminder
}

// One-line "what this proves" subtext shown under each evidence-locker slot label.
export const EVIDENCE_DESCRIPTIONS = {
  order_confirmation_screenshot: "The confirmation a customer sees right after ordering.",
  payment_receipt_email: "The receipt sent after payment, with the amount and order details.",
  refund_confirmation_email: "How you confirm a refund to the customer.",
  return_policy_document: "The return terms the cardholder agreed to at purchase.",
  account_creation_email: "The welcome or account-setup email a customer receives.",
  checkout_screen_screenshot: "The pricing, terms, and disclosures shown before purchase.",
  tracking_page_screenshot: "The shipment tracking page the customer can see.",
  cancellation_flow_screenshot: "Your self-serve cancellation flow.",
  renewal_reminder_email: "The advance notice you send before a renewal charge."
}
